import { useContext, useEffect, useState } from "react";
import { DrawerContext, UserContext } from "@CONTEXTS";
import {
  Form,
  Input,
  Button,
  TreeSelect,
  Spin,
  message,
  Switch,
  Upload,
  Alert,
  Row,
  Col,
  Card,
} from "antd";
import { useGET } from "@API2";
import { AddressEndpoints } from "@ADDRESS";
import XLSX from "xlsx";
import { InboxOutlined } from "@ant-design/icons";
import { AiOutlineCloudDownload } from "react-icons/ai";
import CreateResultModal from "./create-result-modal";
import ErrorModalTable from "../../../../create/delivery/upload-modal/error-modal-table";
import { exportExcel } from "@HELPERS";

const { Dragger } = Upload;
const { SHOW_CHILD } = TreeSelect;

const DeliveryDetail = () => {
  const [form] = Form.useForm();
  const { data, setClose } = useContext(DrawerContext);
  const { data: warehousesData, loading: warehousesLoading } = useGET(
    `${AddressEndpoints.warehouses}`,
    true
  );
  const { mutate } = useContext(UserContext);
  const { data: authorities, loading: authoritiesLoading } = useGET(
    "api/options/authorities"
  );
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [uploadedData, setUploadedData] = useState(false);
  const [createResultModal, setCreateResultModal] = useState(false);

  useEffect(() => {
    if (!authoritiesLoading) {
      const tree = authorities
        .filter((x) => !x.parent)
        .map((item) => ({
          title: item.name,
          value: item.key,
          key: item.key,
          children: authorities
            .filter((y) => y.parent === item.key)
            .map((z) => ({
              title: z.name,
              value: z.key,
              key: z.key,
            })),
        }));
      setTreeData(tree);
    }
  }, [
    data,
    form,
    authorities,
    authoritiesLoading,
    warehousesData,
    warehousesLoading,
  ]);

  const handleParseData = (data) => {
    if (!data || data.length < 1) {
      message.error("Excelde yüklenebilecek bir kişi bulunamadı.");
      return false;
    }
    const arr = [];
    const errorObj = {};
    data.forEach((item, index) => {
      errorObj[index] = [];

      if (!item["Ad"]) {
        errorObj[index].push("Kişi Adı eksik");
      }
      if (!item["Soyad"]) {
        errorObj[index].push("Kişi Soyadı eksik");
      }
      if (!item["Email"]) {
        errorObj[index].push("Email eksik");
      }

      if (errorObj[index].length === 0) {
        let obj = {
          firstName: item["Ad"],
          lastName: item["Soyad"],
          email: item["Email"].toString(),
          role: "USER",
          status: "ACTIVE",
          warehouses: item["Depo"]
            ? item["Depo"]
                .toString()
                .split(/[,\.\-]/)
                .map((w) => w.trim())
            : [],
        };

        arr.push(obj);
      }
    });

    if (Object.values(errorObj).flat().length > 0) {
      return setErrorModal(errorObj);
    }

    setUploadedData(arr);
  };
  const downloadWarehouses = () => {
    exportExcel(
      "Tanımlı Depo Listesi",
      [
        { title: "Depo Kodu", key: "warehouseId" },
        { title: "Depo Adı", key: "warehouseName" },
        { title: "Açık Adres", key: "address.addressLine1" },
        { title: "İl", key: "address.city" },
        { title: "İlçe", key: "address.town" },
        { title: "Mahalle", key: "address.district" },
      ],
      warehousesData
    );
  };

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      const arr = [];
      if (values.authorities) {
        values.authorities.forEach((item) => {
          // parentı bul ve daha önce eklenmemişse pushla.
          const that = authorities.find((x) => x.key === item);
          const parent = authorities.find((x) => x.key === that.parent);
          if (parent && !arr.includes(parent.key)) {
            arr.push(parent.key);
          }
          arr.push(item);
        });
      }

      setLoading(true);

      const uploadArr = [];
      uploadedData.forEach((item) => {
        uploadArr.push({
          ...item,
          ...values,
          authorities: arr,
        });
      });
      setCreateResultModal(uploadArr);

      setLoading(false);
    });
  };
  const props = {
    name: "file",
    multiple: false,
    showUploadList: false,
    action: false,
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    beforeUpload: () => {
      return false;
    },
    onChange(info) {
      if (typeof FileReader !== "undefined") {
        const reader = new FileReader();
        if (reader.readAsBinaryString) {
          reader.onload = () => {
            const workbook = XLSX.read(reader.result, { type: "binary" });
            const firstSheet = workbook.SheetNames[0];
            const excelRows = XLSX.utils.sheet_to_row_object_array(
              workbook.Sheets[firstSheet]
            );
            handleParseData(excelRows);
          };
          reader.readAsBinaryString(info.file);
        }
      }
      return false;
    },
  };

  return (
    <>
      {errorModal && (
        <ErrorModalTable
          result={errorModal}
          onClose={() => setErrorModal(false)}
        />
      )}
      {createResultModal && (
        <CreateResultModal
          data={createResultModal}
          onClose={() => {
            mutate();
            setClose();
            setCreateResultModal(false);
          }}
        />
      )}
      <Spin spinning={loading}>
        <Form layout="vertical" form={form}>
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message="Yüklenilen tüm kullanıcılar seçilen yetki ve şifre ile tanımlanacaktır."
          />
          <Form.Item
            name="password"
            label="Şifre"
            rules={[
              {
                required: data?.id ? false : true,
                message: "geçerli bir şifre giriniz.",
              },
              () => ({
                validator(_, value) {
                  if (!data?.id && !value) {
                    return Promise.reject();
                  }
                  if (value && value.length < 8) {
                    return Promise.reject("Şifre en az 8 haneli olmalı!");
                  }
                  if (value && !/[a-z]/.test(value)) {
                    return Promise.reject(
                      "Şifre en az bir küçük harf içermeli!"
                    );
                  }
                  if (value && !/[A-Z]/.test(value)) {
                    return Promise.reject(
                      "Şifre en az bir büyük harf içermeli!"
                    );
                  }
                  if (value && !/[0-9]/.test(value)) {
                    return Promise.reject("Şifre en az bir rakam içermeli!");
                  }
                  if (
                    value &&
                    !/[!@#$%^&*(),.":{}|<>_\-+=\[\]\\\/]/.test(value)
                  ) {
                    return Promise.reject(
                      "Şifre en az bir özel karakter içermeli!"
                    );
                  }
                  if (value && /\$/.test(value)) {
                    return Promise.reject("Şifre $ işareti içeremez!");
                  }
                  if (value && /\s/.test(value)) {
                    return Promise.reject("Şifre boşluk içeremez!");
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="authorities"
            label="Yetkiler"
            rules={[{ required: true, message: "Yetki seçiniz." }]}
          >
            <TreeSelect
              treeCheckable
              showCheckedStrategy={SHOW_CHILD}
              placeholder="Yetki seçiniz"
              treeData={treeData}
              multiple
              maxTagCount={0}
              maxTagPlaceholder={(values) => `${values.length} YETKİ SEÇİLİ`}
              style={{ textTransform: "none" }}
            />
          </Form.Item>

          <Form.Item
            name="onlySavedReceiversDeliveries"
            label="Manuel Alıcı Kaydı Girilmesin"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          {uploadedData.length > 0 ? (
            <>
              <Alert
                type="warning"
                showIcon
                message={`${uploadedData.length} adet kullanıcıyı yüklemek üzeresiniz.`}
              />

              <div style={{ marginTop: 24 }}>
                <Row gutter={24}>
                  <Col span={24}>
                    <Card size="small">
                      <p>
                        Yükleme işlemi başladıktan sonra lütfen sayfayı
                        kapatmayınız. Bu işlem biraz zaman alabilir.
                      </p>
                      <Form.Item>
                        <Button
                          block
                          type="primary"
                          onClick={() => handleSave()}
                        >
                          Yükle
                        </Button>
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <>
              <Alert
                type="info"
                showIcon
                message="Excel ile kullanıcı yükleyebilmek için örnek excel şablonunu doldurmanız gerekmektedir."
                action={
                  <Button
                    size="small"
                    type="primary"
                    icon={<AiOutlineCloudDownload />}
                    onClick={() =>
                      window.open("/files/Toplu Kullanıcı Excel.xlsx")
                    }
                  >
                    Örnek Şablonu İndir
                  </Button>
                }
              />
              <div style={{ marginTop: 16 }}>
                <Dragger {...props}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Dosya yüklemek için buraya tıklayın veya dosyanızı
                    sürükleyip bu alana bırakın.
                  </p>
                </Dragger>
              </div>
              <Alert
                style={{ marginTop: 16 }}
                type="warning"
                showIcon
                message="Kullanıcılara depo eşleşmesi yapmak için tanımlı depo kodunu kullanmanız gerekmektedir. Kullanıcıya birden fazla depoya yetki vermek için yükleyeceğiniz kullanıcı listesine depo kodlarını virgülle ayırarak kaydetmeniz gerekmektedir."
                action={
                  <Button
                    size="small"
                    type="primary"
                    icon={<AiOutlineCloudDownload />}
                    onClick={() => downloadWarehouses()}
                  >
                    Tanımlı Depo Listesini İndir
                  </Button>
                }
              />
            </>
          )}
        </Form>
      </Spin>
    </>
  );
};
export default DeliveryDetail;
